/** @jsxImportSource @emotion/react */
import {
  Card,
  ListItem,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@quickbit/qb-design-system';
import { useState } from 'react';
import { useAsync } from 'react-use';
import { QBLoader } from 'components';
import { getProfileHistory } from '../../../api';
import { HistoryDifferenceCard } from './HistoryDifferenceCard';
import { OnboardingTable } from './OnboardingTable';

interface Props {
  userReference: string;
}

export const History = ({ userReference }: Props) => {
  const { value: history, loading } = useAsync(async () => {
    return getProfileHistory(userReference);
  }, [userReference]);
  const { spacing, palette, shape } = useTheme();

  const [tab, setTab] = useState<'profile' | 'onboarding' | 'login'>('profile');

  return (
    <>
      {loading && <QBLoader />}
      {history && (
        <>
          <div css={{ marginBottom: spacing.xxs }}>
            <ToggleButtonGroup
              onChange={(option) =>
                setTab(option as 'profile' | 'onboarding' | 'login')
              }
              selected={tab}
              toggleOptions={[
                { label: 'Profile & KYC', value: 'profile' },
                { label: 'Onboarding', value: 'onboarding' },
                { label: 'Login', value: 'login' },
              ]}
            />
          </div>
          <div
            css={[
              {
                display: 'grid',
                gap: spacing.xxs,
                position: 'relative',
                gridTemplateColumns:
                  tab === 'onboarding' || tab === 'profile' ? '1fr 1fr' : '1fr',
              },
            ]}
          >
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: spacing.xxs,
                padding: spacing.xxxs,
                borderRadius: shape.borderRadius,
                background: palette.grey.x50,
              }}
            >
              {tab === 'profile' && (
                <>
                  <Typography variant="heading2">Profile History</Typography>
                  {history.profileHistory.map((record, idx) => (
                    <HistoryDifferenceCard
                      key={idx}
                      record={record}
                      previousRecord={
                        idx + 1 >= history.profileHistory.length
                          ? undefined
                          : history.profileHistory[idx + 1]
                      }
                    />
                  ))}
                </>
              )}
              {tab === 'login' && (
                <>
                  <Typography variant="heading2">Login History</Typography>
                  {history.loginHistory.map((login, idx) => (
                    <Card key={idx}>
                      <div
                        css={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Typography>
                          {login.createdAt.toLocaleString()}
                        </Typography>
                        <span
                          css={{
                            borderRadius: shape.borderRadius,
                            padding: `calc(${spacing.xxxs} / 4) ${spacing.xxxs}`,
                            background: login.success
                              ? palette.success.x200
                              : palette.danger.x200,
                          }}
                        >
                          <Typography>
                            {login.success ? 'Success' : 'Failure'}
                          </Typography>
                        </span>
                      </div>
                      <div
                        css={{
                          backgroundColor: palette.grey.x100,
                          borderRadius: shape.borderRadius,
                          padding: spacing.xxxs,
                        }}
                      >
                        <ListItem
                          title="IP Address"
                          content={login.ipAddress}
                          css={{ padding: spacing.xxxs }}
                        />
                        <ListItem
                          title="Login Transaction ID"
                          content={login.loginTransactionId}
                          css={{ padding: spacing.xxxs }}
                        />
                        <ListItem
                          title="Device"
                          content={login.deviceName}
                          css={{ padding: spacing.xxxs }}
                        />
                        <ListItem
                          title="login method"
                          content={login.authMethod ?? 'None/unknown'}
                          css={{ padding: spacing.xxxs }}
                        />
                      </div>
                    </Card>
                  ))}
                </>
              )}
            </div>

            {tab === 'onboarding' && (
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: spacing.xxs,
                  padding: spacing.xxxs,
                  borderRadius: shape.borderRadius,
                  background: palette.grey.x50,
                }}
              >
                <Typography variant="heading2">Onboarding History</Typography>
                {history.onboardingHistory.map((onboarding, idx) => (
                  <Card key={idx}>
                    <div css={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Typography>
                        {onboarding.createdAt.toLocaleString()}
                      </Typography>
                    </div>
                    <OnboardingTable onboarding={onboarding} />
                  </Card>
                ))}
              </div>
            )}
            {tab === 'profile' && (
              <>
                <div
                  css={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing.xxs,
                    padding: spacing.xxxs,
                    borderRadius: shape.borderRadius,
                    background: palette.grey.x50,
                  }}
                >
                  <Typography variant="heading2">KYC History</Typography>
                  {history.kycHistory.map((record, idx) => (
                    <HistoryDifferenceCard
                      key={idx}
                      record={record}
                      previousRecord={
                        idx + 1 >= history.kycHistory.length
                          ? undefined
                          : history.kycHistory[idx + 1]
                      }
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
};
